import { useEffect, useState, useMemo } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { CalendarDays, ChevronLeft, ChevronRight as ChevronRightIcon, Clock, Heart, Search, Sparkles, X } from 'lucide-react'
import { contentApi, type ContentCategory, type ContentListItem } from '../../lib/api'
import { getCategoryVisual } from '../../lib/categoryIcons'
import ContentCard from '../../components/ContentCard'
import EmptyState from '../../components/EmptyState'
import { usePageTitle } from '../../hooks/usePageTitle'

const PAGE_SIZE = 10

/** 列表项骨架屏（左缩略图 + 右两行文字） */
const CardSkeleton = () => (
  <div className="bg-white rounded-2xl border border-gray-100 p-3 flex gap-3 animate-pulse">
    <div className="w-28 h-20 bg-gray-100 rounded-xl flex-shrink-0" />
    <div className="flex-1 space-y-2 py-0.5">
      <div className="h-3.5 bg-gray-100 rounded w-full" />
      <div className="h-3.5 bg-gray-100 rounded w-2/3" />
      <div className="h-3 bg-gray-100 rounded w-1/3 mt-1.5" />
    </div>
  </div>
)

const Knowledge = () => {
  usePageTitle()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [tree, setTree] = useState<ContentCategory[]>([])
  const [items, setItems] = useState<ContentListItem[]>([])
  const [featured, setFeatured] = useState<ContentListItem[]>([])
  const [events, setEvents] = useState<ContentListItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  // 搜索输入本地状态 + 300ms 防抖，避免每个字符触发一次请求
  const [keywordInput, setKeywordInput] = useState(params.get('keyword') || '')

  const keyword = params.get('keyword') || ''
  const page = Number(params.get('page') || 1)

  // 一级分类（金刚区）
  const level1 = useMemo(() => tree.filter(c => c.level === 1), [tree])

  // 专题活动分类（code=reserved）：分类树只返回启用中的分类，隐藏后此处自然为空 → 板块不展示
  const eventCategory = useMemo(() => level1.find(c => c.code === 'reserved' || c.name === '专题活动'), [level1])

  // 全局精选推荐（与分类无关，仅首次加载取一次）
  useEffect(() => {
    contentApi.listPublic({ is_recommended: true, page: 1, page_size: 6 })
      .then(res => setFeatured(res.items || []))
      .catch(() => setFeatured([]))
  }, [])

  // 专题活动内容：仅当专题活动分类处于启用状态时拉取
  useEffect(() => {
    if (!eventCategory) { setEvents([]); return }
    contentApi.listPublic({ category_id: eventCategory.id, page: 1, page_size: 6 })
      .then(res => setEvents(res.items || []))
      .catch(() => setEvents([]))
  }, [eventCategory])

  useEffect(() => {
    contentApi.getCategoryTree().then(setTree).catch(() => setTree([]))
  }, [])

  useEffect(() => {
    setLoading(true); setError('')
    contentApi.listPublic({
      keyword: keyword || undefined,
      page,
      page_size: PAGE_SIZE,
    }).then(res => {
      setItems(res.items)
      setTotal(res.total)
    }).catch(e => setError(e.message || '加载失败'))
      .finally(() => setLoading(false))
  }, [keyword, page])

  const updateParam = (k: string, v: string) => {
    const np = new URLSearchParams(params)
    if (v) np.set(k, v); else np.delete(k)
    if (k !== 'page') np.delete('page')
    setParams(np)
  }

  // 输入防抖 300ms 后同步到 URL 参数
  useEffect(() => {
    const t = setTimeout(() => {
      if (keywordInput !== (params.get('keyword') || '')) {
        updateParam('keyword', keywordInput)
      }
    }, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keywordInput])

  const totalPages = Math.ceil(total / PAGE_SIZE)

  return (
    <div className="min-h-screen bg-gray-50 pb-6">
      {/* Hero 头图：渐变绿 + 装饰 */}
      <div className="relative bg-gradient-to-br from-green-700 via-green-600 to-emerald-500 overflow-hidden">
        {/* 装饰元素 */}
        <div className="absolute right-[-40px] top-[-30px] w-44 h-44 rounded-full bg-white/10 animate-soft-pulse" />
        <div className="absolute right-16 top-10 w-20 h-20 rounded-full bg-white/10 animate-soft-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute left-[30%] bottom-[-30px] w-24 h-24 rounded-full bg-emerald-300/20" />

        <div className="relative z-10 px-4 pt-6 pb-11 max-w-3xl mx-auto">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full bg-white/15 text-green-50 backdrop-blur-sm border border-white/20">
                <Sparkles className="w-3 h-3" />眉山疾控 · 权威发布
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-white mt-2 tracking-wide">健康科普中心</h1>
              <p className="text-green-100 text-[13px] mt-1">守护您和家人的健康，从科学知识开始</p>
            </div>
          </div>

          {/* 搜索框 */}
          <div className="relative mt-4">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              value={keywordInput}
              onChange={e => setKeywordInput(e.target.value)}
              placeholder="搜索科普内容、关键字…"
              className="w-full pl-11 pr-11 py-3 bg-white rounded-2xl shadow-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-white/30 transition-shadow"
            />
            {keywordInput && (
              <button
                onClick={() => setKeywordInput('')}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 p-1 text-gray-300 hover:text-gray-500"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* 快捷入口 */}
          <div className="flex items-center gap-2 mt-3.5">
            <Link to="/user/knowledge/favorites" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-white/15 hover:bg-white/25 rounded-full text-white border border-white/20 backdrop-blur-sm transition-colors">
              <Heart className="w-3 h-3" />我的收藏
            </Link>
            <Link to="/user/knowledge/history" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-white/15 hover:bg-white/25 rounded-full text-white border border-white/20 backdrop-blur-sm transition-colors">
              <Clock className="w-3 h-3" />阅读历史
            </Link>
            <span className="ml-auto text-[11px] text-green-100/90 hidden sm:block">共收录 {total} 篇科普内容</span>
          </div>
        </div>
        {/* 底部圆弧过渡 */}
        <div className="absolute bottom-0 left-0 right-0 h-5">
          <svg viewBox="0 0 1440 40" preserveAspectRatio="none" className="w-full h-full">
            <path d="M0,40 C360,0 1080,0 1440,40 L1440,40 L0,40 Z" fill="#f9fafb" />
          </svg>
        </div>
      </div>

      <div className="px-4 -mt-4 relative z-10 max-w-3xl mx-auto space-y-3">
        {/* 分类金刚区：点击进入分类列表页 */}
        {level1.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-2 py-4">
            <div className="grid grid-cols-5 gap-y-4">
              {level1.slice(0, 10).map((c, i) => {
                const v = getCategoryVisual(c.name, i)
                return (
                  <button
                    key={c.id}
                    onClick={() => navigate(`/user/knowledge/category/${c.id}`)}
                    className="flex flex-col items-center gap-1.5 group"
                  >
                    <div className={`w-12 h-12 rounded-2xl ${v.bg} ${v.fg} flex items-center justify-center transition-transform group-hover:scale-105 group-active:scale-95`}>
                      <v.Icon className="w-6 h-6" />
                    </div>
                    <span className="text-[11px] text-gray-600 leading-none">{c.name}</span>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* 专题活动：仅当分类启用且有活动内容时展示（分类隐藏则整个板块消失） */}
        {eventCategory && events.length > 0 && (
          <div>
            <div className="flex items-center justify-between px-1 mb-2.5">
              <h2 className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
                <span className="w-1 h-4 bg-gradient-to-b from-orange-500 to-amber-400 rounded-full inline-block" />
                <CalendarDays className="w-4 h-4 text-orange-500" />
                专题活动
              </h2>
              <Link
                to={`/user/knowledge/category/${eventCategory.id}`}
                className="text-[11px] text-gray-400 hover:text-orange-500 flex items-center gap-0.5 transition-colors"
              >
                查看全部 <ChevronRightIcon className="w-3 h-3" />
              </Link>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {events.map(item => (
                <div key={item.id} className="w-56 flex-shrink-0 snap-start">
                  <ContentCard item={item} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 精选推荐：全局固定展示，不随分类/筛选变化（左右滑动横滑条） */}
        {featured.length > 0 && (
          <div>
            <div className="flex items-center justify-between px-1 mb-2.5">
              <h2 className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
                <span className="w-1 h-4 bg-gradient-to-b from-green-500 to-emerald-400 rounded-full inline-block" />
                精选推荐
              </h2>
              <span className="text-[11px] text-gray-400">为你精选的优质内容 · 左右滑动</span>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {featured.map(item => (
                <div key={item.id} className="w-56 flex-shrink-0 snap-start">
                  <ContentCard item={item} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 内容列表：搜索结果或全部最新 */}
        {error ? (
          <EmptyState variant="error" description={error} action={{ label: '重试', onClick: () => updateParam('page', String(page)) }} />
        ) : loading ? (
          <div className="space-y-2.5">
            {Array.from({ length: 5 }).map((_, i) => <CardSkeleton key={i} />)}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title={keyword ? '未找到相关内容' : '暂无内容'}
            description={keyword ? '换个关键词试试' : '内容上架后将在这里展示'}
          />
        ) : (
          <>
            <div className="flex items-center justify-between px-1">
              <span className="text-xs text-gray-500">
                {keyword ? <>“<b className="text-gray-700">{keyword}</b>” 的搜索结果</> : '全部内容'}
              </span>
              <span className="text-[11px] text-gray-400">共 <b className="text-gray-700">{total}</b> 条</span>
            </div>
            {/* 单列横排列表：左缩略图 + 右标题/信息，移动端优先 */}
            <div className="space-y-2.5">
              {items.map(item => (
                <ContentCard key={item.id} item={item} variant="compact" />
              ))}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-1.5 pt-3">
                <button
                  disabled={page <= 1}
                  onClick={() => updateParam('page', String(page - 1))}
                  className="p-2 rounded-full bg-white border border-gray-200 disabled:opacity-40 hover:border-green-300 hover:text-green-700 transition-colors"
                ><ChevronLeft className="w-4 h-4" /></button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const start = Math.max(1, Math.min(page - 2, totalPages - 4))
                  return start + i
                }).filter(p => p >= 1 && p <= totalPages).map(p => (
                  <button
                    key={p}
                    onClick={() => updateParam('page', String(p))}
                    className={`w-7 h-7 rounded-full text-[13px] font-medium transition-colors ${
                      p === page
                        ? 'bg-green-600 text-white shadow-sm shadow-green-200'
                        : 'bg-white border border-gray-200 text-gray-600 hover:border-green-300 hover:text-green-700'
                    }`}
                  >{p}</button>
                ))}
                <button
                  disabled={page >= totalPages}
                  onClick={() => updateParam('page', String(page + 1))}
                  className="p-2 rounded-full bg-white border border-gray-200 disabled:opacity-40 hover:border-green-300 hover:text-green-700 transition-colors"
                ><ChevronRightIcon className="w-4 h-4" /></button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default Knowledge
